import { useEffect, useState } from "react";
import { apiFetch } from "../services/api";
import { createLogger } from "../lib/logger";

const alog = createLogger("admin");

const currentMonth = () => new Date().toISOString().slice(0, 7);

interface CardState {
  loading: boolean;
  error: string | null;
  data: unknown;
}

const INITIAL: CardState = { loading: true, error: null, data: null };

/** Fetch a JSON admin endpoint into card state. */
async function loadJson(path: string): Promise<CardState> {
  try {
    const res = await apiFetch(path);
    if (res.status === 403) {
      return { loading: false, error: "Accès réservé aux administrateurs.", data: null };
    }
    if (!res.ok) {
      return { loading: false, error: `HTTP ${res.status}`, data: null };
    }
    return { loading: false, error: null, data: await res.json() };
  } catch (e) {
    alog.error(`admin fetch ${path} failed`, { err: String(e) });
    return { loading: false, error: "Endpoint injoignable.", data: null };
  }
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 120 }}>
      <span style={{ color: "#6b7280", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </span>
      <span style={{ color: "#e5e7eb", fontSize: 22, fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function Card({
  title,
  state,
  children,
}: {
  title: string;
  state: CardState;
  children?: (data: unknown) => React.ReactNode;
}) {
  return (
    <section
      style={{
        background: "#0f0f0f",
        border: "1px solid #1f2937",
        borderRadius: 10,
        padding: "1rem 1.25rem",
      }}
    >
      <h2 style={{ color: "#9ca3af", fontSize: 13, fontWeight: 700, margin: "0 0 0.75rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {title}
      </h2>
      {state.loading && <p style={{ color: "#6b7280", fontSize: 13 }}>Chargement…</p>}
      {!state.loading && state.error && (
        <p style={{ color: "#f87171", fontSize: 13 }}>{state.error}</p>
      )}
      {!state.loading && !state.error && children?.(state.data)}
    </section>
  );
}

export default function AdminDashboard() {
  const [prewarm, setPrewarm] = useState<CardState>(INITIAL);
  const [contextMetrics, setContextMetrics] = useState<CardState>(INITIAL);
  const [chatMetrics, setChatMetrics] = useState<CardState>(INITIAL);
  const [cost, setCost] = useState<CardState>(INITIAL);

  const refresh = () => {
    setPrewarm(INITIAL);
    setContextMetrics(INITIAL);
    setChatMetrics(INITIAL);
    setCost(INITIAL);
    void loadJson("/admin/prewarm/status").then(setPrewarm);
    void loadJson("/admin/context-metrics").then(setContextMetrics);
    void loadJson("/chat-metrics").then(setChatMetrics);
    // Cost report is CSV, not JSON — handled separately.
    void (async () => {
      try {
        const res = await apiFetch(`/admin/cost-reports?month=${currentMonth()}`, {
          headers: { Accept: "text/csv" },
        });
        if (res.status === 403) {
          setCost({ loading: false, error: "Accès réservé aux administrateurs.", data: null });
          return;
        }
        if (!res.ok) {
          setCost({ loading: false, error: `HTTP ${res.status}`, data: null });
          return;
        }
        const csv = await res.text();
        const header = csv.split("\n")[0] ?? "";
        const totalMatch = header.match(/total_usd=([\d.]+)/);
        const lines = csv
          .split("\n")
          .filter((l) => l && !l.startsWith("#") && !l.startsWith("id,")).length;
        setCost({
          loading: false,
          error: null,
          data: { totalUsd: totalMatch ? parseFloat(totalMatch[1]!) : 0, lines },
        });
      } catch {
        setCost({ loading: false, error: "Endpoint injoignable.", data: null });
      }
    })();
  };

  useEffect(refresh, []);

  return (
    <div style={{ padding: "1.5rem", fontFamily: "system-ui, sans-serif", maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem" }}>
        <div>
          <h1 style={{ color: "#f3f4f6", fontSize: 22, fontWeight: 700, margin: 0 }}>Administration</h1>
          <p style={{ color: "#6b7280", fontSize: 13, margin: "4px 0 0" }}>
            Métriques et coûts — visible uniquement pour les administrateurs.
          </p>
        </div>
        <button
          onClick={refresh}
          style={{
            background: "#1d4ed8",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "6px 14px",
            fontSize: 13,
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          Actualiser
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1rem" }}>
        <Card title="Coûts du mois" state={cost}>
          {(d) => {
            const c = d as { totalUsd: number; lines: number };
            return (
              <div style={{ display: "flex", gap: "2rem" }}>
                <Metric label="Total USD" value={`$${c.totalUsd.toFixed(2)}`} />
                <Metric label="Lignes" value={c.lines} />
                <Metric label="Mois" value={currentMonth()} />
              </div>
            );
          }}
        </Card>

        <Card title="Pool de préchauffage" state={prewarm}>
          {(d) => {
            const p = d as Record<string, unknown>;
            return (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "2rem" }}>
                {Object.entries(p).slice(0, 6).map(([k, v]) => (
                  <Metric key={k} label={k} value={String(v)} />
                ))}
              </div>
            );
          }}
        </Card>

        <Card title="Métriques de contexte" state={contextMetrics}>
          {(d) => (
            <pre style={{ color: "#9ca3af", fontSize: 12, margin: 0, overflowX: "auto", whiteSpace: "pre-wrap" }}>
              {JSON.stringify(d, null, 2).slice(0, 800)}
            </pre>
          )}
        </Card>

        <Card title="Métriques de chat" state={chatMetrics}>
          {(d) => (
            <pre style={{ color: "#9ca3af", fontSize: 12, margin: 0, overflowX: "auto", whiteSpace: "pre-wrap" }}>
              {JSON.stringify(d, null, 2).slice(0, 800)}
            </pre>
          )}
        </Card>
      </div>
    </div>
  );
}
